import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import {
  User,
  Mail,
  Phone,
  Building,
  MapPin,
  Calendar,
  Edit2,
  Save,
  X,
  CheckCircle,
  AlertCircle,
  Loader2,
  Camera,
  Globe,
  Clock,
  Briefcase,
  Home,
  Navigation,
  Flag,
  LogOut,
  Copy,
  Check,
  Upload,
  Image as ImageIcon,
  Trash2,
} from "lucide-react";
import { getUserProfile, setCredentials } from "../../store/slices/authSlice";
import { userService } from "../../api/services/userService";
import { useToast } from "../../components/common/ThemeToaster";

const UserProfile = () => {
  const dispatch = useDispatch();
  const { user, isLoading: authLoading } = useSelector((state) => state.auth);
  const { success: toastSuccess, error: toastError } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("profile");
  const [profileData, setProfileData] = useState(null);
  const [isProfileLoading, setIsProfileLoading] = useState(true);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [profilePictureBase64, setProfilePictureBase64] = useState(null);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    alternativePhone: "",
    company: "",
    position: "",
    address: "",
    country: "",
  });
  const [fieldErrors, setFieldErrors] = useState({});

  // Format phone number to E.164 format
  const formatToE164 = (phone) => {
    if (!phone) return undefined;
    const digits = phone.replace(/\D/g, "");
    if (digits.length === 0) return undefined;
    return `+${digits}`;
  };

  // Validate E.164 phone format
  const isValidE164 = (phone) => {
    if (!phone) return true;
    return /^\+\d{10,15}$/.test(phone);
  };

  // Fetch user profile from API
  const fetchUserProfile = async () => {
    try {
      setIsProfileLoading(true);
      const response = await userService.getProfile();
      console.log("Profile fetch response:", response);

      let userData = response?.user || response?.data?.user || response;

      if (userData && userData.id) {
        setProfileData(userData);
        setFormData({
          firstName: userData.firstName || "",
          lastName: userData.lastName || "",
          phone: userData.phone || "",
          alternativePhone: userData.alternativePhone || "",
          company: userData.company || "",
          position: userData.position || "",
          address: userData.address || "",
          country: userData.country || "",
        });

        // Set profile picture from BLOB if available
        if (userData.profilePictureBase64) {
          setProfilePictureBase64(userData.profilePictureBase64);
        }
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
      if (error.response?.status === 401) {
        localStorage.clear();
        window.location.href = "/user/login";
      } else {
        toastError("Failed to load profile. Please refresh the page.");
      }
    } finally {
      setIsProfileLoading(false);
    }
  };

  useEffect(() => {
    if (user && user.id) {
      setProfileData(user);
      setFormData({
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        phone: user.phone || "",
        alternativePhone: user.alternativePhone || "",
        company: user.company || "",
        position: user.position || "",
        address: user.address || "",
        country: user.country || "",
      });
      if (user.profilePictureBase64) {
        setProfilePictureBase64(user.profilePictureBase64);
      }
      setIsProfileLoading(false);
    } else {
      fetchUserProfile();
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (fieldErrors[name]) {
      setFieldErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handlePhoneChange = (e) => {
    let value = e.target.value;
    value = value.replace(/[^\d+]/g, "");
    if (value && !value.startsWith("+")) {
      value = "+" + value.replace(/\D/g, "");
    }
    setFormData((prev) => ({ ...prev, phone: value }));
  };

  const handleAlternativePhoneChange = (e) => {
    let value = e.target.value;
    value = value.replace(/[^\d+]/g, "");
    if (value && !value.startsWith("+")) {
      value = "+" + value.replace(/\D/g, "");
    }
    setFormData((prev) => ({ ...prev, alternativePhone: value }));
  };

  const handleProfilePictureUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/webp",
    ];
    if (!allowedTypes.includes(file.type)) {
      toastError("Only image files are allowed (JPEG, PNG, GIF, WEBP)");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toastError("Image size must be less than 5MB");
      return;
    }

    setUploadingImage(true);
    try {
      const response = await userService.updateProfilePicture(file);
      const updatedUser = response?.user || response?.data?.user || response;

      if (updatedUser?.profilePictureBase64) {
        setProfilePictureBase64(updatedUser.profilePictureBase64);
        setProfileData(updatedUser);
        await dispatch(getUserProfile());
        toastSuccess("Profile picture updated successfully!");
      }
    } catch (error) {
      console.error("Upload error:", error);
      toastError(
        error.response?.data?.message || "Failed to upload profile picture",
      );
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleRemoveProfilePicture = async () => {
    if (
      !window.confirm("Are you sure you want to remove your profile picture?")
    ) {
      return;
    }

    setUploadingImage(true);
    try {
      const response = await userService.removeProfilePicture();
      const updatedUser = response?.user || response?.data?.user || response;

      setProfilePictureBase64(null);
      setProfileData(updatedUser);
      await dispatch(getUserProfile());
      toastSuccess("Profile picture removed successfully!");
    } catch (error) {
      console.error("Remove error:", error);
      toastError(
        error.response?.data?.message || "Failed to remove profile picture",
      );
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setFieldErrors({});

    const formattedPhone = formatToE164(formData.phone);
    const formattedAlternativePhone = formatToE164(formData.alternativePhone);

    if (formattedPhone && !isValidE164(formattedPhone)) {
      toastError("Phone number must be in E.164 format (e.g., +1234567890)");
      setIsLoading(false);
      return;
    }

    try {
      const updateData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formattedPhone,
        alternativePhone: formattedAlternativePhone,
        company: formData.company || undefined,
        position: formData.position || undefined,
        address: formData.address || undefined,
        country: formData.country || undefined,
      };

      console.log("Updating profile with:", updateData);
      const response = await userService.updateProfile(updateData);
      console.log("Update response:", response);

      const updatedUser = response?.user || response?.data?.user || response;

      if (updatedUser) {
        localStorage.setItem("user", JSON.stringify(updatedUser));
        setProfileData(updatedUser);
        if (updatedUser.profilePictureBase64) {
          setProfilePictureBase64(updatedUser.profilePictureBase64);
        }
        await dispatch(getUserProfile());
        toastSuccess("Profile updated successfully!");
        setIsEditing(false);
      }
    } catch (error) {
      console.error("Update profile error:", error);

      if (error.response?.data?.errors) {
        const errors = error.response.data.errors;
        const fieldErrorMap = {};
        errors.forEach((err) => {
          fieldErrorMap[err.path] = err.msg;
        });
        setFieldErrors(fieldErrorMap);
        toastError("Please check the form for errors");
      } else {
        toastError(error.response?.data?.message || "Failed to update profile");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toastSuccess("Copied to clipboard!");
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getStatusBadge = (status) => {
    const statuses = {
      active: {
        color:
          "bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400",
        icon: CheckCircle,
      },
      pending: {
        color:
          "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400",
        icon: Clock,
      },
      blocked: {
        color: "bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400",
        icon: AlertCircle,
      },
      inactive: {
        color: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400",
        icon: X,
      },
    };
    return statuses[status?.toLowerCase()] || statuses.pending;
  };

  const currentUser = profileData || user;
  const statusBadge = getStatusBadge(currentUser?.status);

  if (isProfileLoading || (authLoading && !currentUser)) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="relative">
            <Loader2
              size={48}
              className="animate-spin text-primary-500 mx-auto mb-4"
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <User size={24} className="text-primary-300" />
            </div>
          </div>
          <p className="text-gray-600 dark:text-gray-400 mt-4 font-medium">
            Loading profile...
          </p>
        </div>
      </div>
    );
  }

  if (!currentUser || !currentUser.id) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center max-w-md">
          <div className="inline-flex p-4 bg-red-100 dark:bg-red-900/20 rounded-full mb-4">
            <AlertCircle size={48} className="text-red-500" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Profile Not Found
          </h3>
          <p className="text-gray-500 dark:text-gray-400">
            Unable to load your profile. Please try logging out and logging back
            in.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
          >
            Refresh Page
          </button>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6 max-w-5xl mx-auto"
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
            My Profile
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Manage your personal information and preferences
          </p>
        </div>
        <button
          onClick={() => setIsEditing(!isEditing)}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium transition-all ${
            isEditing
              ? "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
              : "bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 text-white shadow-lg shadow-primary-600/20"
          }`}
        >
          {isEditing ? <X size={18} /> : <Edit2 size={18} />}
          <span>{isEditing ? "Cancel" : "Edit Profile"}</span>
        </button>
      </div>

      {!isEditing ? (
        <div className="space-y-6">
          {/* Profile Header */}
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
              <div className="relative">
                <div className="w-24 h-24 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-2xl flex items-center justify-center shadow-lg overflow-hidden">
                  {profilePictureBase64 ? (
                    <img
                      src={profilePictureBase64}
                      alt={currentUser.firstName}
                      className="w-full h-full object-cover rounded-2xl"
                    />
                  ) : (
                    <User size={40} className="text-white" />
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                  onChange={handleProfilePictureUpload}
                  className="hidden"
                />
                {profilePictureBase64 && (
                  <button
                    onClick={handleRemoveProfilePicture}
                    disabled={uploadingImage}
                    className="absolute -bottom-2 -left-2 p-1.5 bg-red-500 dark:bg-red-600 rounded-full border border-white dark:border-gray-800 shadow-sm hover:bg-red-600 transition-colors disabled:opacity-50"
                    title="Remove profile picture"
                  >
                    <Trash2 size={12} className="text-white" />
                  </button>
                )}
              </div>

              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-3 mb-2">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                    {currentUser.firstName} {currentUser.lastName}
                  </h2>
                  <span
                    className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${statusBadge.color}`}
                  >
                    {React.createElement(statusBadge.icon, { size: 12 })}
                    {currentUser.status?.charAt(0).toUpperCase() +
                      currentUser.status?.slice(1) || "Active"}
                  </span>
                </div>
                <p className="text-gray-500 dark:text-gray-400 flex items-center gap-2">
                  <Mail size={14} />
                  {currentUser.email}
                  <button
                    onClick={() => copyToClipboard(currentUser.email)}
                    className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                    title="Copy email"
                  >
                    <Copy size={12} className="text-gray-400" />
                  </button>
                </p>
                <p className="text-sm text-gray-400 mt-1 flex items-center gap-2">
                  <Calendar size={12} />
                  Member since {formatDate(currentUser.createdAt)}
                </p>
              </div>
            </div>
          </div>

          {/* Profile Info Tabs */}
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden">
            <div className="flex gap-4 border-b border-gray-100 dark:border-gray-800 px-6 pt-4">
              {["profile", "address"].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`pb-3 px-1 text-sm font-medium transition-colors border-b-2 ${
                    activeTab === tab
                      ? "border-primary-600 text-primary-600"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>

            <div className="p-6">
              {activeTab === "profile" && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <InfoCard
                    icon={User}
                    label="First Name"
                    value={currentUser.firstName}
                  />
                  <InfoCard
                    icon={User}
                    label="Last Name"
                    value={currentUser.lastName}
                  />
                  <InfoCard
                    icon={Mail}
                    label="Email"
                    value={currentUser.email}
                    copyable
                  />
                  <InfoCard
                    icon={Phone}
                    label="Phone"
                    value={currentUser.phone || "Not provided"}
                  />
                  <InfoCard
                    icon={Phone}
                    label="Alternative Phone"
                    value={currentUser.alternativePhone || "Not provided"}
                  />
                  <InfoCard
                    icon={Briefcase}
                    label="Position"
                    value={currentUser.position || "Not provided"}
                  />
                  <InfoCard
                    icon={Building}
                    label="Company"
                    value={currentUser.company || "Not provided"}
                  />
                </div>
              )}

              {activeTab === "address" && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <InfoCard
                    icon={Home}
                    label="Address"
                    value={currentUser.address || "Not provided"}
                  />
                  <InfoCard
                    icon={Globe}
                    label="Country"
                    value={currentUser.country || "Not provided"}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Activity Timeline */}
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Clock size={18} className="text-primary-600" />
              Recent Activity
            </h3>
            <div className="space-y-4">
              {currentUser.lastLoginAt && (
                <div className="flex items-start gap-3 p-3 hover:bg-gray-50 dark:hover:bg-gray-800/50 rounded-xl transition-colors">
                  <div className="p-2 bg-green-100 dark:bg-green-900/20 rounded-lg">
                    <LogOut size={14} className="text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      Last Login
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatDate(currentUser.lastLoginAt)} at{" "}
                      {new Date(currentUser.lastLoginAt).toLocaleTimeString()}
                    </p>
                  </div>
                </div>
              )}
              {currentUser.createdAt && (
                <div className="flex items-start gap-3 p-3 hover:bg-gray-50 dark:hover:bg-gray-800/50 rounded-xl transition-colors">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                    <User size={14} className="text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      Account Created
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatDate(currentUser.createdAt)}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        /* Edit Form */
        <form
          onSubmit={handleSubmit}
          className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
            Edit Profile Information
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Last Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Phone Number
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handlePhoneChange}
                className={`w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all ${
                  fieldErrors.phone
                    ? "border-red-500"
                    : "border-gray-200 dark:border-gray-700"
                }`}
                placeholder="+1234567890"
              />
              {fieldErrors.phone && (
                <p className="text-xs text-red-500 mt-1">{fieldErrors.phone}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Format: +1234567890 (include country code)
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Alternative Phone
              </label>
              <input
                type="tel"
                name="alternativePhone"
                value={formData.alternativePhone}
                onChange={handleAlternativePhoneChange}
                className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                placeholder="+19876543210"
              />
              <p className="text-xs text-gray-500 mt-1">
                Format: +1234567890 (optional)
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Company
              </label>
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                placeholder="Your Company Inc."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Position
              </label>
              <input
                type="text"
                name="position"
                value={formData.position}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                placeholder="Purchasing Manager"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Address
              </label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                placeholder="Street address"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Country
              </label>
              <input
                type="text"
                name="country"
                value={formData.country}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                placeholder="United States"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-8 pt-4 border-t border-gray-100 dark:border-gray-800">
            <button
              type="button"
              onClick={() => setIsEditing(false)}
              className="px-6 py-3 border border-gray-200 dark:border-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 text-white font-medium rounded-xl transition-all active:scale-95 disabled:opacity-50 shadow-lg shadow-primary-600/20"
            >
              {isLoading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Save size={18} />
                  <span>Save Changes</span>
                </>
              )}
            </button>
          </div>
        </form>
      )}
    </motion.div>
  );
};

// Info Card Component
const InfoCard = ({ icon: Icon, label, value, copyable }) => {
  const [copied, setCopied] = useState(false);
  const { success: toastSuccess } = useToast();

  const copyToClipboard = () => {
    if (!copyable || value === "Not provided") return;
    navigator.clipboard.writeText(value);
    setCopied(true);
    toastSuccess("Copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl group">
      <div className="p-2 bg-white dark:bg-gray-900 rounded-lg shadow-sm">
        <Icon size={16} className="text-primary-600" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-gray-500 mb-1">{label}</p>
        <div className="flex items-center gap-2">
          <p className="font-medium text-gray-900 dark:text-white truncate">
            {value}
          </p>
          {copyable && value !== "Not provided" && (
            <button
              onClick={copyToClipboard}
              className="opacity-0 group-hover:opacity-100 p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-all"
              title="Copy"
            >
              {copied ? (
                <Check size={12} className="text-green-500" />
              ) : (
                <Copy size={12} className="text-gray-400" />
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
